/**
 * Drawing under a fixed crosshair — `docs/property-maps-v2.md` §6.2.
 *
 * **Fingers cover what they trace.** On a phone the thing you are placing is
 * under the thing placing it, so a point tapped onto a map is a point you
 * cannot see until you lift. The answer is to stop moving the finger and move
 * the *map*: a crosshair sits at the centre of the screen, one finger pans
 * the property under it, and **Add point** drops a vertex exactly where the
 * crosshair is.
 *
 * **It is an adapter, not a tool.** The six tools in this folder already know
 * what a click means; what changes on a phone is where the click comes from.
 * So this takes a `DrawTool` and turns `addPoint()` into the pointer sequence
 * that tool already understands — which is why a rectangle drawn with a
 * crosshair and a rectangle drawn with a mouse are the same rectangle, tested
 * by the same tests, with no second state machine to drift.
 */
import type { DrawTool, ScreenPoint } from './types.js'

export interface Crosshair {
  /** Drop a vertex where the crosshair is. */
  addPoint(at: ScreenPoint): void
  /** ↶ — take the last one back. */
  undoPoint(): void
  /** ✓ — the shape, or null when there are not enough points yet. */
  finish(): ReturnType<DrawTool['finish']>
  cancel(): void
  draft(): ReturnType<DrawTool['draft']>
  /** How many points have been placed, for the drawing bar's readout. */
  readonly points: number
  /**
   * Whether ✓ would give something worth saving.
   *
   * The bar greys ✓ rather than hiding it: a control that appears only once
   * the shape is finishable is one nobody finds the first time.
   */
  readonly complete: boolean
}

/**
 * A path tool under the crosshair: every Add point is a click at the centre.
 *
 * `Area` and `Line` are a series of clicks on a desktop and a series of
 * **Add point**s here, which is the same series — the tool cannot tell.
 */
export function crosshairPath(tool: DrawTool): Crosshair {
  let points = 0
  return {
    addPoint(at) {
      tool.onPointerDown({ point: at })
      tool.onPointerUp({ point: at })
      points += 1
    },
    undoPoint() {
      if (points === 0) return
      tool.onKey({ key: 'Backspace' })
      points -= 1
    },
    finish: () => tool.finish(),
    cancel: () => { tool.cancel(); points = 0 },
    draft: () => tool.draft(),
    get points() { return points },
    get complete() { return tool.draft()?.complete ?? false },
  }
}

/**
 * A drag tool under the crosshair: two Add points are the two corners.
 *
 * `Rect` and `Circle` are one drag on a desktop, which a crosshair cannot
 * make — so the first Add point is the press and the second is the release,
 * with a move in between so the tool sees the shape grow. Corner A, pan,
 * corner B; centre, pan to the edge.
 *
 * `finish()` before the second point gives nothing, which is what keeps ✓
 * from making a rectangle with no width.
 */
export function crosshairDrag(tool: DrawTool): Crosshair {
  let first: ScreenPoint | null = null
  let second: ScreenPoint | null = null

  return {
    addPoint(at) {
      if (!first) {
        first = at
        tool.onPointerDown({ point: at })
        return
      }
      second = at
      tool.onPointerMove({ point: at })
      tool.onPointerUp({ point: at })
    },
    undoPoint() {
      /*
       * A drag has no "last point" to pop — taking one back means starting
       * the shape again, which is the honest thing for two corners: there is
       * no state between "one corner" and "none".
       */
      tool.cancel()
      first = null
      second = null
    },
    finish: () => (second ? tool.finish() : null),
    cancel: () => { tool.cancel(); first = null; second = null },
    draft: () => tool.draft(),
    get points() { return (first ? 1 : 0) + (second ? 1 : 0) },
    get complete() { return second !== null && (tool.draft()?.complete ?? false) },
  }
}

/**
 * A point tool under the crosshair: one Add point places it.
 *
 * `autoFinish` is already true for a marker and a label, so the editor has
 * the shape the moment the point lands — this exists so the drawing bar can
 * treat all three families the same way rather than branching on the tool.
 */
export function crosshairPoint(tool: DrawTool): Crosshair {
  let placed = false
  return {
    addPoint(at) {
      tool.onPointerDown({ point: at })
      tool.onPointerUp({ point: at })
      placed = true
    },
    undoPoint() { tool.cancel(); placed = false },
    finish: () => (placed ? tool.finish() : null),
    cancel: () => { tool.cancel(); placed = false },
    draft: () => tool.draft(),
    get points() { return placed ? 1 : 0 },
    get complete() { return placed },
  }
}

/** Which of the three a tool needs, by what its gesture is. */
export function crosshairFor(tool: DrawTool): Crosshair {
  if (tool.kind === 'marker' || tool.kind === 'label') return crosshairPoint(tool)
  if (tool.kind === 'rect' || tool.kind === 'circle') return crosshairDrag(tool)
  return crosshairPath(tool)
}
